import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CContainer,
  CFormInput,
  CImage,
  CInputGroup,
  CInputGroupText,
  CLink,
  CLoadingButton,
  CRow
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft } from "@coreui/icons";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Formik } from "formik";
import { AppToaster } from "../../../components/toaster";
import logo from "../../../assets/images/logo.png";

// IMPORTANT: import your API function and the async effect hook
import { getTeamLogos, requestPasswordResetLink } from "../../../api/APIClient";
import useAsyncEffect from "use-async-effect";

const isWhiteLabelHostname = () =>
  window.location.hostname !== "dash.robopost.app" &&
  window.location.hostname !== "localhost";

const ForgotPassword = () => {
  const [isSent, setIsSent] = useState(false);
  const { t } = useTranslation();

  // State for storing the dynamically loaded logo
  const [logoComponent, setLogoComponent] = useState(null);

  // For navigation (redirecting to /404 if needed)
  const navigate = useNavigate();

  // Dynamically load the logo based on hostname
  useAsyncEffect(async () => {
    if (isWhiteLabelHostname()) {
      try {
        const logos = await getTeamLogos(window.location.hostname);
        setLogoComponent(
          <CImage src={logos.software_logo_url} align="center" height={50} />
        );
      } catch (error) {
        console.error("Failed to fetch white-labeled team logos:", error);
        // If you want to show a fallback logo instead of redirecting, you could do so here.
        // For now, mimic the same logic as your Login code:
        navigate("/404");
      }
    } else {
      // Default RoboPost logo
      setLogoComponent(
        <CLink href="https://www.robopost.app">
          <CImage src={logo} align="center" height={50} />
        </CLink>
      );
    }
  }, []);

  const handleSubmit = async (values) => {
    try {
      await requestPasswordResetLink(values.email, window.location.hostname);
      setIsSent(true);
    } catch (e) {
      const errorCode = e.response?.data?.detail || e.code;
      if (errorCode === "auth/user-not-found") {
        AppToaster.show({
          message: t("message:email:emailNotFound"),
          intent: "danger",
          timeout: 2500,
        });
      } else {
        AppToaster.show({
          message: errorCode,
          intent: "danger",
          timeout: 2500,
        });
      }
      console.log(e);
    }
  };

  return (
    <div className="forgot-password-wrapper min-vh-100 d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          {/* This is where we render the dynamic logo */}
          <CCol className="clearfix mb-4" md={12}>
            {logoComponent /* <-- dynamic logo */}
          </CCol>

          <CCol md={10} lg={8}>
            <CCardGroup>
              <CCard className="p-4">
                <CCardBody>
                  {!isSent ? (
                    <Formik
                      validateOnChange={false}
                      validateOnBlur={false}
                      initialValues={{
                        email: "",
                      }}
                      validationSchema={Yup.object().shape({
                        email: Yup.string()
                          .email(t("message:email:invalidEmail"))
                          .required(t("message:requiredValue")),
                      })}
                      onSubmit={handleSubmit}
                    >
                      {({
                          values,
                          errors,
                          setFieldValue,
                          handleSubmit,
                        }) => (
                        <div>
                          <h4>{t("forgotPassword:title")}</h4>
                          <p className="text-medium-emphasis mb-4">
                            {t("forgotPassword:description")}
                          </p>
                          <CInputGroup className="mb-4">
                            <CInputGroupText>@</CInputGroupText>
                            <CFormInput
                              placeholder={t("placeholder:email")}
                              onChange={(event) => {
                                setFieldValue("email", event.target.value);
                              }}
                              invalid={errors.email ? true : false}
                              feedbackInvalid={errors.email}
                            />
                          </CInputGroup>
                          <CRow className="mb-5">
                            <CCol xs={12} className="text-center d-grid gap-2">
                              <CLoadingButton
                                onClick={() => {
                                  handleSubmit(values);
                                }}
                                color="success"
                              >
                                {t("forgotPassword:send")}
                              </CLoadingButton>
                            </CCol>
                          </CRow>
                        </div>
                      )}
                    </Formik>
                  ) : (
                    <div>
                      <h3>{t("forgotPassword:sentEmail")}</h3>
                    </div>
                  )}

                  <div className="mt-4 text-start">
                    <Link to={"/login"}>
                      <CIcon icon={cilArrowLeft} />
                      &nbsp;{t("forgotPassword:backToLogin")}
                    </Link>
                  </div>
                </CCardBody>
              </CCard>
            </CCardGroup>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  );
};

export default ForgotPassword;
